import { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Pillar {
  icon: string;
  title: string;
  description: string;
}

interface AIBlockProps {
  eyebrow: string;
  headline: string;
  description: string;
  pillars: Pillar[];
}

const TABS = ['Who We Are', 'What We Do', 'How It Works', 'Why Solar Now'];

const AI_CONTENT = [
  {
    heading: 'Star Solar Specialists — Kentuckiana\'s Trusted Solar Team',
    body: 'Star Solar Specialists is a family-owned and operated solar installation company headquartered at 509 Ohio Avenue, Jeffersonville, Indiana 47130. Founded in 2015, we are professionally licensed and insured in both Indiana and Kentucky. Our mission is to bring affordable, high-quality solar power to homeowners, businesses, and nonprofits across the Kentuckiana region — Southern Indiana and North Central Kentucky.',
  },
  {
    heading: 'Residential, Commercial & Nonprofit Solar Installation',
    body: 'We design and install custom solar power systems for residential homes, commercial buildings, and nonprofit organizations. Our services include full system design, permitting, installation, and post-install support. We also offer battery storage solutions for energy independence and grid backup. Every system is engineered for maximum energy production and return on investment specific to your property and location.',
  },
  {
    heading: 'From Consultation to Grid Connection — We Handle Everything',
    body: 'Our process starts with a free, no-obligation consultation where we analyze your energy usage, roof orientation, and financial goals. We then design a custom system, handle all permits and utility interconnection paperwork, and complete the installation — typically in 1–3 days on-site. Most installations go from consultation to grid connection in 4–8 weeks.',
  },
  {
    heading: 'The Federal Tax Credit Is 30% — And It\'s Available Right Now',
    body: 'The current federal Investment Tax Credit (ITC) allows homeowners and businesses to deduct 30% of the total cost of their solar system from their federal taxes. Businesses can also depreciate up to 85% of the installation cost. Combined with rising utility rates in Indiana and Kentucky, there has never been a better time to invest in solar energy. Most homeowners in Kentuckiana see full payback within 7–10 years.',
  },
];

const AIBlock = ({ eyebrow, headline, description, pillars }: AIBlockProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-white py-32" aria-label="About Star Solar Specialists">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div
          ref={ref}
          className={`max-w-2xl mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-amber-500" />
            <span className="text-amber-600 text-xs font-semibold tracking-[0.25em] uppercase">{eyebrow}</span>
          </div>
          <h2 className="font-serif text-5xl md:text-6xl text-zinc-900 leading-tight mb-5">
            {headline}
          </h2>
          <p className="text-zinc-500 text-lg leading-relaxed">{description}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left — Structured AI content with tabs */}
          <div
            className={`transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            {/* Tab selector */}
            <div className="flex flex-wrap gap-2 mb-8">
              {TABS.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(i)}
                  className={`text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === i
                      ? 'bg-amber-500 text-zinc-950'
                      : 'bg-stone-100 text-zinc-600 hover:bg-stone-200'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="bg-stone-50 rounded-3xl p-8 border border-stone-100 min-h-[220px]">
              <h3 className="font-serif text-xl text-zinc-900 font-semibold mb-4 leading-snug">
                {AI_CONTENT[activeTab].heading}
              </h3>
              <p className="text-zinc-600 text-sm leading-relaxed">
                {AI_CONTENT[activeTab].body}
              </p>
            </div>

            {/* Data facts */}
            <dl className="mt-8 grid grid-cols-2 gap-4">
              {[
                { dt: 'Headquarters', dd: 'Jeffersonville, IN 47130' },
                { dt: 'Founded', dd: '2015 — Family Owned & Operated' },
                { dt: 'Licensed In', dd: 'Indiana & Kentucky' },
                { dt: 'Tax Incentive', dd: '30% Federal Investment Tax Credit' },
              ].map(({ dt, dd }) => (
                <div key={dt} className="bg-stone-50 rounded-2xl p-5 border border-stone-100">
                  <dt className="text-amber-600 font-bold text-xs uppercase tracking-wide mb-1.5">{dt}</dt>
                  <dd className="text-zinc-700 text-sm leading-snug">{dd}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Right — Pillars */}
          <div
            className={`space-y-5 transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="relative rounded-3xl overflow-hidden h-52 mb-5">
              <img
                src="https://public.readdy.ai/ai/img_res/b60bbb75b617100fb8d16828cdad1366.jpg"
                alt="Star Solar Specialists team installing solar"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/60 to-transparent" />
              <div className="absolute bottom-5 left-6">
                <p className="text-white font-serif text-lg font-semibold">Professional Installation</p>
                <p className="text-zinc-300 text-xs">Licensed crews — Southern IN &amp; N. Central KY</p>
              </div>
            </div>

            {pillars.map((pillar, i) => (
              <div
                key={pillar.title}
                className={`flex gap-5 items-start transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${400 + i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-2xl flex-shrink-0 border border-amber-100">
                  <i className={`${pillar.icon} text-amber-600 text-xl`} />
                </div>
                <div>
                  <h3 className="font-semibold text-zinc-900 text-base mb-1">{pillar.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{pillar.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIBlock;
